import * as React from 'react'
import { PanelLeft } from 'lucide-react'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface SidebarContextValue {
    state: 'expanded' | 'collapsed'
    isMobile: boolean
    open: boolean
    setOpen: (open: boolean) => void
    toggleSidebar: () => void
}

const SidebarContext = React.createContext<SidebarContextValue | undefined>(
    undefined
)

export function useSidebar() {
    const context = React.useContext(SidebarContext)
    if (!context) {
        return {
            state: 'expanded' as const,
            isMobile: false,
            open: true,
            setOpen: () => {},
            toggleSidebar: () => {},
        }
    }
    return context
}

interface SidebarProviderProps {
    children: React.ReactNode
    defaultOpen?: boolean
}

export function SidebarProvider({
    children,
    defaultOpen = true,
}: SidebarProviderProps) {
    const [open, setOpen] = React.useState(defaultOpen)
    const [isMobile, setIsMobile] = React.useState(false)

    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    const toggleSidebar = React.useCallback(() => {
        setOpen(prev => !prev)
    }, [])

    const state = open ? 'expanded' : 'collapsed'

    return (
        <SidebarContext.Provider
            value={{
                state,
                isMobile,
                open,
                setOpen,
                toggleSidebar,
            }}
        >
            {children}
        </SidebarContext.Provider>
    )
}

// SheetProvider wrapper for mobile sidebar
function SheetProvider({ children }: { children: React.ReactNode }) {
    const { isMobile, open, setOpen } = useSidebar()

    if (isMobile) {
        return (
            <Sheet open={open} onOpenChange={setOpen}>
                {children}
            </Sheet>
        )
    }

    return <>{children}</>
}

// Main Sidebar component
interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
    side?: 'left' | 'right'
    variant?: 'sidebar' | 'floating' | 'inset'
    collapsible?: 'offcanvas' | 'icon' | 'none'
}

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
    (
        {
            className,
            side = 'left',
            variant = 'sidebar',
            collapsible = 'offcanvas',
            children,
            ...props
        },
        ref
    ) => {
        const { isMobile, open } = useSidebar()

        if (isMobile) {
            return (
                <SheetContent
                    side={side}
                    className={cn(
                        'w-[260px] p-0 [&>button]:hidden',
                        variant === 'floating' && 'bg-transparent border-0',
                        className
                    )}
                >
                    <div
                        ref={ref}
                        className={cn('flex h-full w-full flex-col', className)}
                        {...props}
                    >
                        {children}
                    </div>
                </SheetContent>
            )
        }

        return (
            <div
                ref={ref}
                className={cn(
                    'group peer fixed inset-y-0 z-10 hidden w-64 -translate-x-full border-r bg-sidebar-background transition-transform duration-300 ease-in-out data-[state=collapsed]:w-16 lg:flex lg:translate-x-0',
                    variant === 'floating' && 'border-0 bg-transparent',
                    variant === 'inset' && 'border-0',
                    className
                )}
                data-state={open ? 'expanded' : 'collapsed'}
                {...props}
            >
                {children}
            </div>
        )
    }
)
Sidebar.displayName = 'Sidebar'

// SidebarTrigger component
const SidebarTrigger = React.forwardRef<
    React.ElementRef<typeof Button>,
    React.ComponentPropsWithoutRef<typeof Button>
>(({ className, ...props }, ref) => {
    const { toggleSidebar, isMobile } = useSidebar()

    if (isMobile) {
        return (
            <Button
                ref={ref}
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7', className)}
                onClick={toggleSidebar}
                {...props}
            >
                <PanelLeft className="h-4 w-4" />
                <span className="sr-only">Toggle Sidebar</span>
            </Button>
        )
    }

    return (
        <Button
            ref={ref}
            variant="ghost"
            size="icon"
            className={cn('h-7 w-7', className)}
            onClick={toggleSidebar}
            {...props}
        >
            <PanelLeft className="h-4 w-4" />
            <span className="sr-only">Toggle Sidebar</span>
        </Button>
    )
})
SidebarTrigger.displayName = 'SidebarTrigger'

// SidebarInset - Main content area
const SidebarInset = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
    const { isMobile } = useSidebar()

    return (
        <div
            ref={ref}
            className={cn(
                'flex w-full flex-1 flex-col',
                !isMobile && 'lg:ml-64 lg:w-[calc(100%-16rem)]',
                className
            )}
            {...props}
        />
    )
})
SidebarInset.displayName = 'SidebarInset'

// SidebarHeader
const SidebarHeader = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            'flex h-16 shrink-0 items-center gap-2 border-b px-4',
            className
        )}
        {...props}
    />
))
SidebarHeader.displayName = 'SidebarHeader'

// SidebarContent
const SidebarContent = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            'flex flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden px-2 py-4',
            className
        )}
        {...props}
    />
))
SidebarContent.displayName = 'SidebarContent'

// SidebarFooter
const SidebarFooter = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            'flex shrink-0 items-center gap-2 border-t px-4 py-4',
            className
        )}
        {...props}
    />
))
SidebarFooter.displayName = 'SidebarFooter'

// SidebarGroup
const SidebarGroup = React.forwardRef<
    HTMLDivElement,
    React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
    <div
        ref={ref}
        className={cn('flex w-full flex-col gap-1', className)}
        {...props}
    />
))
SidebarGroup.displayName = 'SidebarGroup'

const SidebarMenu = React.forwardRef<
    HTMLUListElement,
    React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
    <ul
        ref={ref}
        className={cn('flex flex-col gap-1 px-0 py-0 w-full', className)}
        {...props}
    />
))
SidebarMenu.displayName = 'SidebarMenu'

const SidebarMenuItem = React.forwardRef<
    HTMLLIElement,
    React.HTMLAttributes<HTMLLIElement>
>(({ className, ...props }, ref) => (
    <li ref={ref} className={cn('w-full', className)} {...props} />
))
SidebarMenuItem.displayName = 'SidebarMenuItem'

const SidebarMenuButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
        asChild?: boolean
        isActive?: boolean
        size?: 'default' | 'sm' | 'lg' | 'icon'
    }
>(
    (
        {
            className,
            asChild = false,
            isActive = false,
            size = 'default',
            ...props
        },
        ref
    ) => {
        const { state } = useSidebar()
        const isCollapsed = state === 'collapsed'

        const sizeClasses = {
            default: 'h-10',
            sm: 'h-9',
            lg: 'h-11',
            icon: 'h-10 w-10',
        }

        if (asChild) {
            return <div className={cn(sizeClasses[size], className)} />
        }

        return (
            <button
                ref={ref}
                className={cn(
                    'flex items-center gap-2 rounded-md px-3 text-sm font-medium transition-colors w-full border-0 outline-none focus:outline-none focus:ring-0',
                    'hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100',
                    isActive &&
                        'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                    sizeClasses[size],
                    isCollapsed && 'justify-center',
                    className
                )}
                {...props}
            />
        )
    }
)
SidebarMenuButton.displayName = 'SidebarMenuButton'

export {
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    Sidebar,
    SidebarTrigger,
    SidebarInset,
    SidebarHeader,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SheetProvider,
}
